/**
 * The stages of an audio recording's processing, and what they say (contract §4). The server
 * reports the stage the latest job is in and, once it failed, the stage it failed in. A job
 * passes through four stages — it waits in the queue, the speech is transcribed, the transcript
 * is cut into passages and indexed for search, the entities are extracted — and is `done` past
 * the last of them. Each stage leaves something that stays when a later one fails: a recording
 * whose entities were not extracted still has its transcript and can be searched. Pure.
 */
import type { ProcessingStageDto } from '@/services/api/cases'

/** Where the latest job of a recording is, as the API names it: the wire's own union. */
export type ProcessingStage = ProcessingStageDto

/** The stages a job passes through, in order; `done` is past the last of them. */
export const RAIL_STAGES = ['queued', 'transcript', 'indexing', 'entities'] as const

export type RailStage = (typeof RAIL_STAGES)[number]

/** What one step of the chain shows: settled, being worked on, not reached, or where it stopped. */
export type StepStatus = 'done' | 'current' | 'waiting' | 'failed'

export interface StageStep {
  stage: RailStage
  status: StepStatus
}

/** The two fields every read of a recording carries; both absent on a server that names no stage. */
export interface StageMarks {
  stage?: ProcessingStage | null
  failedStage?: ProcessingStage | null
}

/** The stages as a whole: the job runs at a stage, stopped at one, or is through all of them. */
export type StageReading =
  | { kind: 'running'; at: RailStage; steps: readonly StageStep[] }
  | { kind: 'failed'; at: RailStage; steps: readonly StageStep[] }
  | { kind: 'done'; steps: readonly StageStep[] }

const ORDER: readonly ProcessingStage[] = [...RAIL_STAGES, 'done']

/** A stage as the API spells it; null for none, or for a word this build does not know. */
export function readStage(value: unknown): ProcessingStage | null {
  return ORDER.includes(value as ProcessingStage) ? (value as ProcessingStage) : null
}

const isRailStage = (stage: ProcessingStage | null | undefined): stage is RailStage =>
  stage != null && stage !== 'done'

/** The four steps with everything before `at` settled and `at` itself shown as `status`. */
function stepsAt(at: RailStage, status: 'current' | 'failed'): StageStep[] {
  const reached = RAIL_STAGES.indexOf(at)
  return RAIL_STAGES.map((stage, index) => ({
    stage,
    status: index < reached ? 'done' : index === reached ? status : 'waiting',
  }))
}

/**
 * What the stages say, or null where the server named none (an older server): the caller then
 * shows what it showed before. A failed stage decides over the stage reached — a failed job
 * reports the same stage in both — and `done` is never a stage a job fails in.
 */
export function stageReading(
  stage: ProcessingStage | null | undefined,
  failedStage: ProcessingStage | null | undefined
): StageReading | null {
  if (isRailStage(failedStage)) {
    return { kind: 'failed', at: failedStage, steps: stepsAt(failedStage, 'failed') }
  }
  if (stage === 'done') {
    return { kind: 'done', steps: RAIL_STAGES.map(each => ({ stage: each, status: 'done' })) }
  }
  if (isRailStage(stage)) return { kind: 'running', at: stage, steps: stepsAt(stage, 'current') }
  return null
}

/** Whether a reading is worth a status of its own: the job still runs, or stopped in a failure. */
export function isLiveReading(
  reading: StageReading | null
): reading is Exclude<StageReading, { kind: 'done' }> {
  return reading !== null && reading.kind !== 'done'
}
